import React, { useEffect, useMemo, useState, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ShieldCheck, GraduationCap, Repeat, Download } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { RoundsPlanCard } from '../components/investors/RoundsPlanCard';
import { InvestmentGrowthSlider } from '../components/investors/InvestmentGrowthSlider';
import { ScrollToTopButton } from '../components/ScrollToTopButton';
import { InvestorPdfModal } from '../components/investors/InvestorPdfModal';
import { ReferredByBanner } from '../components/investors/ReferredByBanner';

// Below-the-fold sections — loaded on-demand so they never enter the
// critical-path chunk that renders the hero. Each becomes its own JS chunk
// which the browser fetches in parallel once the shell paints.
const MondayPulseSection = lazy(() => import('../components/investors/MondayPulseSection'));
const ExpressInterestSection = lazy(() => import('../components/investors/ExpressInterestSection'));
const InvestorAccessGate = lazy(() => import('../components/investors/InvestorAccessGate'));

/**
 * HiBFF — Public Prospective-Investor Gateway (/investors)
 *
 * Redesign Jun 2026 — pivots from dark-mode to the warm pastel system used
 * across the founder's internal Command Centre, refreshed copy blocks, and
 * an interactive Milestone Tracker. All numeric values still flow from
 * `GET /api/investors/public-snapshot`, which reads the canonical
 * `cap_table_config` doc. Public route — no auth. The "Enter Active Investor
 * Vault" button continues to gate at /investor-vault (6-digit passcode).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Brand palette ──────────────────────────────────────────────────────────
const PALETTE = {
  bgBase: '#F8F9FA',          // Warm linen cream
  textMain: '#0B1528',        // Deep plum-slate
  textMuted: '#475569',
  coral: '#0A2540',
  mint: '#0D5E4E',
  lavender: '#1D4ED8',
  cardBg: '#FFFFFF',
  cardBorder: 'rgba(15,23,42,0.08)',
};

// ── Formatters ─────────────────────────────────────────────────────────────
const fmtUsers = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : `${n}`;
const fmtMoneyShort = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `${SYM}${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${SYM}${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${SYM}${(n / 1000).toFixed(0)}k`;
  return `${SYM}${Number(n).toLocaleString()}`;
};
const fmtMoneyFull = (n) => (n === null || n === undefined) ? '—' : `${SYM}${Number(n).toLocaleString()}`;

// ── Card primitive ────────────────────────────────────────────────────────
const PastelCard = ({ icon: Icon, iconBg, title, body, testid }) => (
  <div
    className="rounded-2xl p-6 transition-shadow hover:shadow-md"
    style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: iconBg }}>
        <Icon size={20} style={{ color: PALETTE.textMain }} />
      </div>
      <h3 className="text-base font-bold" style={{ color: PALETTE.textMain }}>{title}</h3>
    </div>
    <p className="text-sm leading-relaxed" style={{ color: PALETTE.textMuted }}>{body}</p>
  </div>
);

// ── Milestone Tracker ─────────────────────────────────────────────────────
const PRICE = 26.49; // $26.49 blended revenue per paying family (UK headline price £28/mo = $35.99 US)
// Valuation layer is USD — it must match the signed ASA cap and the deck.
const SYM = '$';
const CUR = 'USD';
const MULTIPLE = 9;
const FLOOR = 11_500_000;
const PIVOT_USERS = Math.ceil(FLOOR / (PRICE * 12 * MULTIPLE)); // = 3,015
const SLIDER_MAX = 1_000_000;

const ANCHOR_QUICKJUMPS = [
  { users: 2500,    label: '2.5k (Convergence Pivot)' },
  { users: 10_000,  label: '10k Milestone' },
  { users: 50_000,  label: '50k Scale Target' },
  { users: 175_000, label: '175k Enterprise Tier' },
  { users: 500_000, label: '500k Market Lead' },
  { users: 1_000_000, label: '1M Institutional Scale' },
];

// Tier names shown inline with each user-count row in the Revenue at Scale
// table. Maps the canonical anchor values to human-readable tier labels so
// the table reads like a milestone narrative, not a flat numeric grid.
const TIER_LABELS = {
  2500:      'Convergence Pivot',
  10_000:    'Early Milestone',
  50_000:    'Scale Target',
  175_000:   'Enterprise Tier',
  300_000:   'Growth Inflection',
  500_000:   'Market Lead',
  1_000_000: 'Institutional Scale',
  5_000_000: 'Generational Scale',
};

/* ── MilestoneTracker slider styles ─────────────────────────────────────
   Hoisted to module scope so the <style> tag is emitted ONCE at mount
   instead of re-injected on every setU render. Prevents CSSOM invalidation
   storms on mobile Chromium that were freezing the tracker after clicks. */
const MILESTONE_SLIDER_STYLES = `
  .hibff-milestone-slider::-webkit-slider-thumb {
    appearance: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${PALETTE.coral};
    border: 4px solid white;
    box-shadow: 0 4px 12px rgba(10, 37, 64, 0.55), 0 0 0 1px rgba(42, 34, 53, 0.08);
    cursor: grab;
    transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  }
  .hibff-milestone-slider::-webkit-slider-thumb:hover {
    transform: scale(1.12);
    box-shadow: 0 6px 16px rgba(10, 37, 64, 0.7), 0 0 0 1px rgba(42, 34, 53, 0.08);
  }
  .hibff-milestone-slider::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.08); }
  .hibff-milestone-slider::-moz-range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${PALETTE.coral};
    border: 4px solid white;
    box-shadow: 0 4px 12px rgba(10, 37, 64, 0.55);
    cursor: grab;
  }
  @keyframes hibff-slider-pulse {
    0%, 100% { transform: scale(1); box-shadow: 0 4px 12px rgba(10, 37, 64, 0.55), 0 0 0 1px rgba(42, 34, 53, 0.08); }
    50% { transform: scale(1.08); box-shadow: 0 4px 18px rgba(10, 37, 64, 0.75), 0 0 0 6px rgba(10, 37, 64, 0.18); }
  }
  .hibff-milestone-slider:not(:hover):not(:focus)::-webkit-slider-thumb {
    animation: hibff-slider-pulse 2.4s ease-in-out infinite;
  }
`;

const MilestoneTracker = React.memo(function MilestoneTracker() {
  const [u, setU] = useState(100_000);
  const { mrr, arr, rawVal, ev, isFloor } = useMemo(() => {
    const mrr = u * PRICE;
    const arr = mrr * 12;
    const rawVal = arr * MULTIPLE;
    const ev = Math.max(FLOOR, rawVal);
    return { mrr, arr, rawVal, ev, isFloor: ev <= FLOOR };
  }, [u]);

  const pctOnTrack = Math.min(100, (u / SLIDER_MAX) * 100);
  const pivotPct = Math.min(100, (PIVOT_USERS / SLIDER_MAX) * 100);

  return (
    <section
      className="rounded-3xl p-8 lg:p-10"
      style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}
      data-testid="milestone-tracker"
      data-section-id="growth-simulator"
    >
      {/* Slider chrome styles — see MILESTONE_SLIDER_STYLES above. Rendering
          the <style> once via dangerouslySetInnerHTML keeps React from
          re-diffing the CSS text on every state change. */}
      <style dangerouslySetInnerHTML={{ __html: MILESTONE_SLIDER_STYLES }} />

      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
        <h2 className="text-xl font-black" style={{ color: PALETTE.textMain }}>Interactive Milestone Tracker</h2>
        <span
          className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: isFloor ? '#ECF0F4' : '#EBF6F3',
            color: isFloor ? '#926829' : '#1E6F4F',
          }}
          data-testid="milestone-status-pill"
        >
          {isFloor ? `${SYM}11,500,000 [Asset Floor Active]` : '[Revenue Scale Active]'}
        </span>
      </div>
      <p className="text-sm mb-6" style={{ color: PALETTE.textMuted }}>
        Drag the slider to see how subscriber growth maps directly to monthly revenue, annual recurring revenue, and enterprise valuation at our <strong>{MULTIPLE}× cap-table multiple</strong>. Floor at <strong>{fmtMoneyShort(FLOOR)}</strong> remains locked until the revenue equation surpasses it.
      </p>

      {/* Slider */}
      <div className="mb-6">
        <div className="flex justify-between text-xs mb-1.5" style={{ color: PALETTE.textMuted }}>
          <span className="font-bold uppercase tracking-wider">Active Subscribers (U)</span>
          <span className="font-black text-lg tabular-nums" style={{ color: PALETTE.textMain }} data-testid="milestone-u-readout">{Number(u).toLocaleString()}</span>
        </div>
        <div className="relative pt-2 pb-12">
          <input
            type="range" min="0" max={SLIDER_MAX} step="100" value={u}
            onChange={e => setU(Number(e.target.value))}
            aria-label="Active Subscribers slider — drag to explore revenue and valuation"
            className="hibff-milestone-slider w-full h-3 rounded-lg appearance-none cursor-pointer touch-manipulation"
            style={{
              background: `linear-gradient(to right, ${PALETTE.coral} 0%, ${PALETTE.coral} ${pctOnTrack}%, ${PALETTE.cardBorder} ${pctOnTrack}%, ${PALETTE.cardBorder} 100%)`,
              accentColor: '#0A2540',
            }}
            data-testid="milestone-slider"
          />
          {/* Floor-breakeven tick — plain-English label so first-time readers
              grok it without needing to reference the paragraph above. */}
          <div className="absolute top-0 pointer-events-none" style={{ left: `calc(${pivotPct}% - 1px)` }}>
            <div className="w-0.5 h-7" style={{ background: PALETTE.mint }} />
            <div className="text-[9px] font-bold -ml-2 mt-1.5 whitespace-nowrap" style={{ color: '#1E6F4F' }}>
              ▲ Floor unlocks at {Number(PIVOT_USERS).toLocaleString()} users
            </div>
          </div>
        </div>
        {/* End labels + drag hint — makes the affordance unmistakable */}
        <div className="flex items-center justify-between mt-4 text-[11px]" style={{ color: PALETTE.textMuted }}>
          <span className="font-semibold tabular-nums">0 users</span>
          <span className="font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: PALETTE.coral }}>
            <span aria-hidden>◀</span> Drag to explore <span aria-hidden>▶</span>
          </span>
          <span className="font-semibold tabular-nums">1,000,000 users</span>
        </div>

        {/* Quick-jump buttons — full-width grid below the tracker so they're
            unmistakably clickable affordances, not decorative pills. */}
        <div className="mt-10" data-testid="milestone-quickjumps">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: PALETTE.textMuted }}>
            Jump to a milestone
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {ANCHOR_QUICKJUMPS.map(a => {
              const active = u === a.users;
              return (
                <button
                  key={a.users}
                  type="button"
                  onClick={() => setU(a.users)}
                  className="group relative rounded-xl px-3 py-3 text-left transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    background: active ? PALETTE.coral : 'white',
                    color: active ? 'white' : PALETTE.textMain,
                    border: `1.5px solid ${active ? PALETTE.coral : 'rgba(15,23,42,0.14)'}`,
                    boxShadow: active
                      ? '0 6px 16px rgba(10, 37, 64, 0.40), 0 2px 4px rgba(10, 37, 64, 0.25)'
                      : '0 1px 2px rgba(42, 34, 53, 0.06), 0 4px 8px rgba(42, 34, 53, 0.04)',
                  }}
                  data-testid={`milestone-jump-${a.users}`}
                >
                  <div
                    className="text-lg font-black tabular-nums leading-none"
                    style={{ color: active ? 'white' : PALETTE.textMain }}
                  >
                    {a.users >= 1_000_000 ? `${a.users / 1_000_000}M` : `${a.users / 1000}k`}
                  </div>
                  <div
                    className="text-[10px] uppercase tracking-wider font-bold mt-1.5 leading-tight"
                    style={{ color: active ? 'rgba(255,255,255,0.85)' : PALETTE.textMuted }}
                  >
                    {a.label.replace(/^\d+\.?\d*[kM]\s*\(?/, '').replace(/\)$/, '').replace(/^Milestone$/, 'Early Milestone')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5" style={{ background: '#EBF6F3' }} data-testid="milestone-mrr">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#1E6F4F' }}>Monthly Revenue (MRR)</div>
          <div className="text-2xl font-black" style={{ color: PALETTE.textMain }}>{fmtMoneyShort(mrr)}</div>
          <div className="text-xs mt-1" style={{ color: PALETTE.textMuted }}>U × ${PRICE} blended / month</div>
        </div>
        <div className="rounded-2xl p-5" style={{ background: '#EFF4FB' }} data-testid="milestone-arr">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#3A5BA0' }}>Annual Recurring Revenue</div>
          <div className="text-2xl font-black" style={{ color: PALETTE.textMain }}>{fmtMoneyShort(arr)}</div>
          <div className="text-xs mt-1" style={{ color: PALETTE.textMuted }}>MRR × 12</div>
        </div>
        <div className="rounded-2xl p-5" style={{ background: '#ECF0F4' }} data-testid="milestone-ev">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#926829' }}>Revenue-Scaled Valuation</div>
          <div className="text-2xl font-black" style={{ color: PALETTE.textMain }}>{fmtMoneyShort(ev)}</div>
          <div className="text-xs mt-1" style={{ color: PALETTE.textMuted }}>
            {isFloor ? 'Asset floor enforced (raw rev below)' : `Raw rev = ${fmtMoneyShort(rawVal)} × ${MULTIPLE}×`}
          </div>
        </div>
      </div>

      <p className="text-[11px] mt-5 leading-relaxed" style={{ color: PALETTE.textMuted }}>
        Calculations are deterministic outputs of the published formula <code style={{ background: PALETTE.bgBase, padding: '2px 6px', borderRadius: 4 }}>V_E = max({fmtMoneyShort(FLOOR)}, U × $26.49 blended × 12 × {MULTIPLE}× illustrative)</code>. Valuation figures in USD, matching the signed ASA; $26.49 is the blended revenue per paying family (UK headline price £28/mo = $35.99 US · £1 ≈ $1.29, Oct 2026). Not an offer of securities. Not financial guidance.
      </p>
    </section>
  );
});

// ── Page ──────────────────────────────────────────────────────────────────
const InvestorOpportunity = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  // Financial-promotion gate: sliders/terms only reveal to self-certified
  // accredited / sophisticated / professional investors (persisted locally,
  // audit-logged server-side).
  const [certified, setCertified] = useState(() => {
    try { return !!localStorage.getItem('hibff_investor_selfcert'); } catch (e) { return false; }
  });
  const handleCertify = ({ region, category }) => {
    try { localStorage.setItem('hibff_investor_selfcert', JSON.stringify({ region, category, ts: Date.now() })); } catch (e) { /* no-op */ }
    fetch(`${BACKEND_URL}/api/investors/self-certify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region, category, confirmed: true }),
    }).catch(() => {});
    setCertified(true);
  };
  useEffect(() => { document.title = 'Investor Portal — HiBFF'; }, []);  useEffect(() => {
    fetch(`${BACKEND_URL}/api/investors/public-snapshot`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSnapshot(d))
      .catch(() => setSnapshot(null));
  }, []);

  const userAnchors = snapshot?.user_anchors || [];
  const monthlyPerUser = snapshot?.monthly_per_user_usd || 0;
  const valuation = snapshot?.valuation_usd;
  // Backend canon uses $ — display follows the visitor's currency (same number worldwide)
  const valuationBasis = snapshot?.valuation_basis ? String(snapshot.valuation_basis).replace(/\$/g, SYM) : null;
  const ownership = snapshot?.ownership_baseline || [];

  // Revenue-at-Scale rows — hoisted to useMemo so the O(N²) recomputation of
  // maxAnnual and per-row derived values only re-runs when the snapshot
  // actually changes, not on every re-render (e.g. from the anonymous
  // section-tracking hook's IntersectionObserver callbacks).
  const rasRows = useMemo(() => {
    if (!userAnchors.length) return [];
    const maxAnnual = (userAnchors[userAnchors.length - 1] || 1) * monthlyPerUser * 12;
    return userAnchors.map((u, i) => {
      const monthly = u * monthlyPerUser;
      const annual = monthly * 12;
      return {
        u,
        monthly,
        annual,
        widthPct: Math.max(2, (annual / maxAnnual) * 100),
        tierName: TIER_LABELS[u] || '',
        isTop: i === userAnchors.length - 1,
        rowIndex: i,
      };
    });
  }, [userAnchors, monthlyPerUser]);

  return (
    <div className="min-h-screen" style={{ background: PALETTE.bgBase, color: PALETTE.textMain }} data-testid="investor-opportunity-page">
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      {/* Back link */}
      <div className="px-6 lg:px-16 pt-8 max-w-6xl mx-auto">
        <Link
          to="/"
          className="text-xs inline-flex items-center gap-1.5 hover:opacity-70 transition-opacity"
          style={{ color: PALETTE.textMuted }}
          data-testid="back-home-link"
        >
          <ArrowRight size={12} className="rotate-180" /> Back to hibff.com
        </Link>
      </div>

      {/* ─── Header / Institutional Frame ─────────────────────────────── */}
      <header className="px-6 lg:px-16 pt-10 pb-14 max-w-4xl mx-auto text-center" data-testid="header-frame" data-section-id="header">
        <ReferredByBanner />
        <div className="inline-flex items-center mb-8">
          <span
            className="px-4 py-2 rounded-full text-[11px] lg:text-xs font-bold uppercase tracking-[0.2em]"
            style={{
              color: '#475569',
              background: '#FFFFFF',
              border: '1px solid rgba(15,23,42,0.12)',
              boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
            }}
            data-testid="header-eyebrow"
          >
            Investor Relations — Private Memorandum
          </span>
        </div>
        {/* HiBFF Logo (registered) — primary brand element */}
        <img
          src="/hibff-logo.png"
          alt="HiBFF logo"
          className="mx-auto mb-5 h-40 lg:h-52 w-auto"
          data-testid="hibff-logo-mark"
        />
        <h1 className="text-xl lg:text-2xl font-bold mb-5" style={{ color: PALETTE.textMain }} data-testid="hibff-tagline">
          Hi Brave Friends Forever
        </h1>
        <p
          className="text-base lg:text-lg italic mx-auto leading-relaxed mb-8"
          style={{ color: PALETTE.textMuted, fontWeight: 500, maxWidth: '720px' }}
          data-testid="hibff-creed"
        >
          &ldquo;Brave takes courage. Courage builds confidence. Confidence builds character. And character is the foundation of friends forever.&rdquo;
        </p>
        <div
          className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3 max-w-xs sm:max-w-none mx-auto sm:mx-0"
          data-testid="investor-hero-ctas"
        >
          <Link
            to="/investor-vault"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-xs font-bold uppercase tracking-[0.08em] transition-all shadow-md hover:shadow-lg active:scale-[0.99]"
            style={{ background: '#0A2540', color: '#fff', border: '1px solid #0A2540' }}
            data-testid="enter-active-vault-btn"
          >
            <Lock size={14} /> Enter Active Investor Vault
          </Link>
          <a
            href="#express-interest"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="express-interest-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-xs font-bold uppercase tracking-[0.08em] transition-all shadow-sm hover:bg-slate-50"
            style={{ background: '#FFFFFF', color: '#0B1528', border: '1px solid #E2E8F0' }}
            data-testid="talk-to-founders-btn"
          >
            <Mail size={14} style={{ color: '#475569' }} /> Talk to the Founders
          </a>
          <button
            type="button"
            onClick={() => setPdfModalOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-xs font-bold uppercase tracking-[0.08em] transition-all hover:bg-slate-200"
            style={{ background: '#F1F5F9', color: '#0F172A', border: '1px solid #E2E8F0' }}
            data-testid="download-onepager-btn"
            aria-haspopup="dialog"
          >
            <Download size={14} style={{ color: '#475569' }} /> Download One-Pager (PDF)
          </button>
        </div>

        {/* ─── Trust strip — uniform institutional credential badges ───── */}
        <div
          className="mt-8 flex flex-wrap items-center justify-center gap-3 w-full max-w-4xl mx-auto"
          data-testid="trust-badges"
        >
          {[
            { id: 'badge-provisional-patents', Icon: ShieldCheck, dot: '#0A2540', label: '3 UK Patent Applications Live (UK IPO)', title: '3 live UK patent applications — GB2617027.4 · GB2622892.4 · GB2622997.1' },
            { id: 'badge-soc2', Icon: Lock, dot: '#0D5E4E', label: 'SOC2-Ready Architecture', title: 'SOC2-ready architecture' },
            { id: 'badge-coppa', Icon: ShieldCheck, dot: '#1D4ED8', label: 'Upgraded COPPA Compliant', title: 'Upgraded COPPA-compliant — exceeds baseline children\u2019s online privacy requirements' },
            { id: 'badge-ocean', Icon: Repeat, dot: '#926829', label: 'Big Five OCEAN Engine', title: 'Big Five OCEAN — behavioural personality model embedded across the AI interaction layer' },
          ].map(({ id, Icon, dot, label, title }) => (
            <span
              key={id}
              className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] whitespace-nowrap transition-all duration-200"
              style={{
                background: '#F8FAFC',
                color: '#0B1528',
                border: '1px solid rgba(15,23,42,0.14)',
                boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
              }}
              data-testid={id}
              title={title}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dot }} />
              <Icon size={13} strokeWidth={2.4} style={{ color: '#475569' }} />
              {label}
            </span>
          ))}
        </div>
      </header>

      {/* ─── Opportunity Cards ────────────────────────────────────────── */}
      <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto" data-section-id="opportunity">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-5" style={{ color: PALETTE.textMuted }}>The Opportunity</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <PastelCard
            testid="card-vision"
            icon={ShieldCheck}
            iconBg={'#EFF4FB'}
            title="Building the Digital Safety Layer for the Next Generation"
            body="HiBFF delivers a highly defensive, end-to-end protected infrastructure that addresses the global crisis in minor-tech safeguarding. By shifting away from passive digital surveillance, our platform couples advanced multi-modal artificial intelligence with closed family-collaboration nodes, establishing a new category in scalable behavioral wellness tech."
          />
          <PastelCard
            testid="card-moat"
            icon={ShieldCheck}
            iconBg={'#EBF6F3'}
            title="Zero-Negligence Safeguarding & Filed IP Fortification"
            body="Built securely from the first line of code to clear institutional SOC2 audit validations. The runtime network deploys a highly customized Claude Sonnet 4.5 wrapper governed by rigorous programmatic filter blockers (NVIDIA NeMo Guardrails). This setup completely blocks toxic interaction, medical/mental health queries, and self-harm prompts before execution. All dual-dashboard workflows, joint family charters, and 180-day countdown reward vaults are covered by three live UK patent applications (GB2617027.4 · GB2622892.4 · GB2622997.1), with further filings planned post-investment."
          />
          <PastelCard
            testid="card-gives"
            icon={GraduationCap}
            iconBg={'#F1F5F9'}
            title="HiBFF for Schools: A Frictionless, Zero-CAC B2B2C Acquisition Model"
            body={`Capital efficiency driven by organic institutional distribution. HiBFF licenses schools worldwide on special institutional pricing — every school's rate is quoted individually (call for your rate; 14-day free pilot, terms negotiated per school) — with no mandates and nothing for the school to administer beyond enrolment. The school framework acts as an authoritative, trusted referral route: every licensed school introduces HiBFF to its parent community with maximum institutional credibility, driving families straight into the £28/mo UK price ($35.99 US) consumer ecosystem at a near-zero cost of customer acquisition. A dual-engine model — predictable B2B licence revenue per school, plus compounding D2C conversion from each school's catchment, everywhere in the world.`}
          />
          <PastelCard
            testid="card-retention"
            icon={Repeat}
            iconBg={'#F5EFE6'}
            title="Membership for Life & Long-Term LTV Compounding"
            body={`Users onboard via 'Maya', an AI-avatar interface that guides them through a 22-question spoken psychometric mapping (BIG5/OCEAN matrix and localized user preference metrics). HiBFF is a secure private space for life — teens create groups, invite friends and grow real friendships inside the platform, new tutors are added over time, and as members grow into adulthood they can recommend features, earning rewards when HiBFF adopts them. Earned rewards are secured in a 180-day countdown vault, anchoring a minimum 6-month lifecycle value (${SYM}159 contracted LTV at the ${SYM}26.49 blended rate) per user block — the floor, not the ceiling, of a lifetime membership.`}
          />
        </div>
      </section>

      {/* ─── Financial detail — gated behind investor self-certification ── */}
      {!certified ? (
        <Suspense fallback={<div className="h-40" aria-hidden />}>
          <InvestorAccessGate onUnlock={handleCertify} />
        </Suspense>
      ) : (
        <>
      {/* ─── Interactive Milestone Tracker ─────────────────────────────── */}
      <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto">
        <MilestoneTracker />
      </section>

      {/* ─── Investment Growth Simulator (SAFE / ASA / CLN discount math) ── */}
      <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto" data-testid="investment-growth-section" data-section-id="investment-growth">
        <InvestmentGrowthSlider
          floor={Number(snapshot?.valuation_usd) || 11_500_000}
          totalShares={Number(snapshot?.total_shares) || 10_000_000}
          perUser={Number(snapshot?.monthly_per_user_usd) || 26.49}
          multiple={Number(snapshot?.revenue_multiple) || 9}
          anchors={snapshot?.user_anchors?.length ? snapshot.user_anchors : undefined}
                    variant="public"
        />
      </section>

      {/* ─── Two-Step Capital Path (live-priced from cap-table) ─────────── */}
      {(snapshot?.rounds_plan || []).length > 0 && (
        <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto" data-testid="rounds-plan-section" data-section-id="rounds-plan">
          <RoundsPlanCard plan={snapshot.rounds_plan} variant="public" />
        </section>
      )}

      {/* ─── Revenue at Scale (canonical anchors from public-snapshot) ─── */}
      <section className="px-6 lg:px-16 pb-14 max-w-4xl mx-auto" data-testid="ras-section" data-section-id="revenue-at-scale">
        <h2 className="text-2xl lg:text-4xl font-black tracking-tight mb-3 leading-[1.1]" style={{ color: PALETTE.textMain }}>
          Revenue at Scale <span style={{ color: PALETTE.coral }}>—</span> Indicative Anchors
        </h2>
        <p className="text-base mb-6" style={{ color: PALETTE.textMuted }}>
          Built at <strong style={{ color: PALETTE.textMain }}>{SYM}{monthlyPerUser}/month per user</strong>. Founder-approved indicative checkpoints — not forecasts, not guidance.
        </p>
        <div className="rounded-2xl overflow-hidden" style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}>
          {/* Header */}
          <div
            className="grid items-center px-3 sm:px-5 py-3 text-[10px] font-bold uppercase tracking-wider"
            style={{
              gridTemplateColumns: '1.6fr 1fr 1.1fr 1.6fr',
              gap: '0.5rem',
              color: PALETTE.textMuted,
              borderBottom: `1px solid ${PALETTE.cardBorder}`,
              background: PALETTE.bgBase + '55',
            }}
          >
            <div>Tier · Users</div>
            <div className="text-right">Monthly</div>
            <div className="text-right">Annual</div>
            <div className="text-right hidden sm:block">Relative Scale</div>
            <div className="text-right sm:hidden">Scale</div>
          </div>
          {/* Rows */}
          {rasRows.length === 0 ? (
            <div className="py-6 px-5 text-center text-xs" style={{ color: PALETTE.textMuted }} data-testid="ras-empty">Loading…</div>
          ) : (
            rasRows.map(({ u, monthly, annual, widthPct, tierName, isTop, rowIndex }) => {
              const i = rowIndex;
              return (
                <div
                  key={u}
                  className="grid items-center px-3 sm:px-5 py-3.5 transition-colors hover:bg-white"
                  style={{
                    gridTemplateColumns: '1.6fr 1fr 1.1fr 1.6fr',
                    gap: '0.5rem',
                    borderTop: i === 0 ? 'none' : `1px solid ${PALETTE.cardBorder}`,
                    background: i % 2 === 0 ? PALETTE.bgBase + '55' : 'transparent',
                  }}
                  data-testid={`ras-row-${u}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm sm:text-base font-black tabular-nums leading-tight truncate" style={{ color: PALETTE.textMain }}>{u.toLocaleString()}</div>
                    {tierName && (
                      <div className="text-[9px] uppercase tracking-wider font-bold mt-0.5 truncate" style={{ color: isTop ? '#926829' : PALETTE.textMuted }}>
                        {tierName}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs sm:text-sm tabular-nums" style={{ color: PALETTE.textMuted }}>
                    {fmtMoneyShort(monthly)}
                  </div>
                  <div className="text-right text-sm sm:text-base font-black tabular-nums" style={{ color: PALETTE.textMain }}>
                    {fmtMoneyShort(annual)}
                  </div>
                  <div className="flex items-center justify-end gap-1.5 sm:gap-2 min-w-0">
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(15,23,42,0.08)' }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${widthPct}%`,
                          background: isTop
                            ? '#0A2540'
                            : '#1E3A5F',
                          boxShadow: isTop ? `0 0 8px ${PALETTE.coral}55` : 'none',
                        }}
                      />
                    </div>
                    <span className="text-[10px] font-bold tabular-nums w-8 sm:w-10 text-right" style={{ color: isTop ? '#926829' : PALETTE.textMuted }}>
                      {widthPct < 1 ? '<1%' : `${widthPct.toFixed(0)}%`}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ─── Valuation + Ownership ────────────────────────────────────── */}
      <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-5" data-section-id="valuation">
        <div
          className="rounded-2xl p-6"
          style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}
          data-testid="valuation-card"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: PALETTE.textMuted }}>Valuation Benchmark</div>
          <div className="text-4xl font-black mb-2" style={{ color: PALETTE.textMain }}>{fmtMoneyShort(valuation)}</div>
          <p className="text-sm leading-relaxed" style={{ color: PALETTE.textMuted }}>
            {valuationBasis || 'Indicative founder benchmark for current planning.'} Not an offer, not a price round, not financial guidance.
          </p>
        </div>
        <div
          className="rounded-2xl p-6"
          style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}
          data-testid="ownership-card"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: PALETTE.textMuted }}>Ownership Baseline</div>
          <ul className="text-sm">
            {ownership.map((h, i) => (
              <li
                key={h.name}
                className="py-2.5 flex justify-between"
                style={{ borderTop: i === 0 ? 'none' : `1px solid ${PALETTE.cardBorder}` }}
                data-testid={`ownership-${h.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span style={{ color: PALETTE.textMain }}>{h.name}</span>
                <span className="font-bold" style={{ color: PALETTE.textMain }}>{Number(h.pct).toFixed(0)}%</span>
              </li>
            ))}
            {ownership.length === 0 && (
              <li className="py-2 text-xs" style={{ color: PALETTE.textMuted }} data-testid="ownership-empty">Loading…</li>
            )}
          </ul>
          <p className="text-xs mt-3" style={{ color: PALETTE.textMuted }}>Pre-investment baseline per founder document.</p>
        </div>
      </section>

      {/* ─── Monday Pulse (lazy) ────────────────────────────────────── */}
      <Suspense fallback={<div className="h-40" aria-hidden />}>
        <MondayPulseSection />
      </Suspense>

      {/* ─── Investor Documents (Nov 2026 finals) ───────────────────── */}
      <section className="px-6 lg:px-16 pb-14 max-w-6xl mx-auto" data-testid="investor-docs-section" data-section-id="investor-docs">
        <h2 className="text-2xl lg:text-4xl font-black tracking-tight mb-3 leading-[1.1]" style={{ color: PALETTE.textMain }}>
          Investor Documents <span style={{ color: PALETTE.coral }}>—</span> Nov 2026
        </h2>
        <p className="text-base mb-6" style={{ color: PALETTE.textMuted }}>
          Start with the two-page manifesto, then open the deck for the instrument that matches your jurisdiction.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { id: 'manifesto', title: 'Investor Manifesto', badge: 'START HERE', desc: 'The mission, the moment and the raise — on two pages.' },
            { id: 'safe-deck', title: 'SAFE Pitch Deck', badge: 'US · SAFE', desc: 'The full presentation for investors subscribing via the SAFE instrument.' },
            { id: 'asa-deck', title: 'ASA Pitch Deck', badge: 'UK · ASA', desc: 'The full presentation for investors subscribing via the UK Advance Subscription Agreement.' },
          ].map((d) => (
            <a
              key={d.id}
              href={`${BACKEND_URL}/api/public/docs/investor/${d.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl p-5 flex flex-col transition-all hover:shadow-md hover:-translate-y-0.5"
              style={{ background: PALETTE.cardBg, border: `1px solid ${PALETTE.cardBorder}` }}
              data-testid={`investor-doc-${d.id}`}
            >
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full self-start mb-3" style={{ background: 'rgba(10,37,64,0.06)', color: PALETTE.textMain }}>
                {d.badge}
              </span>
              <span className="text-base font-black mb-1" style={{ color: PALETTE.textMain }}>{d.title}</span>
              <span className="text-xs leading-relaxed flex-1" style={{ color: PALETTE.textMuted }}>{d.desc}</span>
              <span className="text-xs font-bold mt-3" style={{ color: PALETTE.coral }}>Open PDF →</span>
            </a>
          ))}
        </div>
      </section>

      {/* ─── Express Interest (in-app lead capture, lazy) ───────────── */}
      <Suspense fallback={<div className="h-40" aria-hidden />}>
        <ExpressInterestSection />
      </Suspense>
        </>
      )}

      {/* ─── Footer ───────────────────────────────────────────────────── */}
      <footer className="px-6 lg:px-16 py-14 max-w-6xl mx-auto text-center" data-testid="footer-cta">
        <h3 className="text-xl font-bold mb-2" style={{ color: PALETTE.textMain }}>Brave conversations, in a safe place.</h3>
        <p className="text-sm" style={{ color: PALETTE.textMuted }}>
          HiBFF is building a category-defining teen safeguarding platform. Submit the form above and we&rsquo;ll be in touch within one business day.
        </p>
      </footer>

      {/* Floating scroll-up button — appears after 400px scroll */}
      <ScrollToTopButton />

      {/* Investor One-Pager PDF download modal — gated on 4 required fields */}
      <InvestorPdfModal open={pdfModalOpen} onClose={() => setPdfModalOpen(false)} />
    </div>
  );
};

export default InvestorOpportunity;

